import {DataObject, DatavisionMetadata, Keywords, PostObject, PostObjectResponse} from "./metadata";
import { StrategicPillar } from "../services/strategic-pillar.service";
import { VeevaTaggedAudience } from "../services/audience.service";

export type Priority = 1 | 2 | 3 | 0

export const PRIORITY_VALUES = [1, 2, 3]
const PRIORITY_LOGIC_MAP = {
    "dataTraining": 1,
    "publicationAsSlides": 2,
    "metisStatements": 3
}

export class Publication extends DataObject {
  // Update to match API response format
  strategicPillars: StrategicPillar[] = [];
  // Audiences tagged to the publication (from Veeva)
  audiences: VeevaTaggedAudience[] = [];
  
    metadata!: DatavisionMetadata
    priority!: Priority
    priorityLinks!: {
        dataTraining: string
        publicationAsSlides: string
        metisStatements: string
    }
    keyWords!: Keywords
    notes!: string
    isVisible!: boolean

    public static priorityLinkIsRequired(fieldName: keyof typeof PRIORITY_LOGIC_MAP, priority: Priority): boolean {
        return priority ? priority <= PRIORITY_LOGIC_MAP[fieldName] : false
    }

    get abstractID(): string {
      return this.metadata.documentNumber;
    }

    get abstractDate(): string {
      return this.metadata.expectedPublicationDate
    }

    toPost(changes: Publication): PublicationPost {
      return new PublicationPost(this, changes);
    }

    get studyPresentations(){
      return this.metadata.studies.map(s => s.studyNumber + (s.studyName? ", " + s.studyName: ""))
    }

    static override parseFromPostResponse(post: PublicationPostResponse): PublicationPostResponse {
      if (post.updatedData) {
        if ((post.updatedData as any).strategicPillars) {
          (post.updatedData as any).strategicPillars = (post.updatedData as any).strategicPillars.map((p: any) => Object.assign({}, p));
        }
        if ((post.updatedData as any).audiences) {
          (post.updatedData as any).audiences = (post.updatedData as any).audiences.map((a: any) => Object.assign({}, a));
        }
      }
      post.updatedData = Object.assign(new Publication(), post.updatedData)
      return post
    }
    
    // Helper getter to maintain backward compatibility with existing code
    get strategicPillar(): StrategicPillar | undefined {
      return this.strategicPillars && this.strategicPillars.length > 0 ? this.strategicPillars[0] : undefined;
    }
}

export interface PublicationEndpointResponse {
    isOld: boolean,
    lastUpdate: string,
    publications: Array<Publication>
}

export const TEST_PUBLICATION: Publication = Object.assign(new Publication(), {
    metadata: {
        documentNumber: 'DV-Docnum123',
        title: 'Title',
        shortTitle: null,
        status: 'Planned',
        authors: ['Author 1', 'Author 2'],
        target: 'Target Journal/Congress',
        congress_acronym: null,
        studies: [
          {
            id: 0,
            studyNumber: 'ML1234567890',
            studyName: "Placeholder study name"
          },
          {
            id: 1,
            studyNumber: 'ML0987654321',
            studyName: "Placeholder study name"
          }],
        expectedPublicationDate: '2024-06-13',
        link: "Link to publication",
    },
    priority: 1,
    priorityLinks: {
        dataTraining: "http://linkX.com",
        metisStatements: "http://linkY.com",
        publicationAsSlides: "http://linkZ.com"
    },
    keyWords: ['Keyword 1', 'Keyword 2', 'Keyword 3'],
    link: "Link to publication",
    notes: "Notes. This can be a looooong text",
    isVisible: true,
  audiences: [],
})

export class PublicationPost extends PostObject{
    // documentNumber is the primary identifier, pID kept for backwards compatibility
    documentNumber!: string
    pID!: string  // Alias for documentNumber, sent for backwards compatibility
    priority: Priority
    priorityLinks: typeof Publication.prototype.priorityLinks | undefined
    notes: typeof Publication.prototype.notes | undefined
    isVisible: typeof Publication.prototype.isVisible | undefined
    strategicPillars?: number[]
    audiences?: number[]

    constructor(source: Publication, newValue: Publication) {
        super(source, newValue)
        if(source.metadata.documentNumber !== newValue.metadata.documentNumber){
          throw Error("PublicationPost: documentNumber cannot be overwritten");
        }
        this.documentNumber = source.metadata.documentNumber
        this.pID = source.metadata.documentNumber  // Alias for backwards compatibility
        this.priority = newValue.priority
        this.priorityLinks = newValue.priorityLinks
        this.notes = newValue.notes
        this.isVisible = newValue.isVisible
    }

  getSourceWrapper(): { (post: PublicationPostResponse): PublicationPostResponse } {
    return Publication.parseFromPostResponse
  }
}

export type PublicationPostResponse = PostObjectResponse<Publication>
